import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Briefcase, X, FolderOpen, Calendar, Hash, Building2, Eye, Archive, RefreshCw } from 'lucide-react';
import { IconButton } from '../../../shared/components/ui/IconButton';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useProjets, useCreerProjet, useDesactiverProjet, useReactiverProjet } from '../hooks/useProjets';
import { useConfirm } from '../../../shared/components/ConfirmDialog/useConfirm';
import { administrationApi } from '../api/administration.api';
import type { Projet } from '../types';

type FiltreStatut = 'tous' | 'actif' | 'inactif';

interface FormProjet {
  code: string;
  nom: string;
  description: string;
  dateDebut: string;
  dateFin: string;
}

export function ProjetsListePage() {
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState<FiltreStatut>('tous');
  const [panelOuvert, setPanelOuvert] = useState(false);

  const filtreApi = filtreStatut === 'tous' ? undefined : { actif: filtreStatut === 'actif' };
  const { data: projets, isLoading, isError, error } = useProjets(filtreApi);
  const { data: organisation } = useQuery({
    queryKey: ['organisation-moi'],
    queryFn: administrationApi.obtenirOrganisation,
  });
  const creerProjet = useCreerProjet();
  const desactiverProjet = useDesactiverProjet();
  const reactiverProjet = useReactiverProjet();

  const confirm = useConfirm();

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormProjet>({
    defaultValues: { code: '', nom: '', description: '', dateDebut: '', dateFin: '' },
  });

  const projetsFiltres = useMemo(() => {
    if (!projets) return [];
    if (!recherche.trim()) return projets;
    const q = recherche.toLowerCase();
    return projets.filter(
      (p: Projet) => p.nom.toLowerCase().includes(q) || p.code.toLowerCase().includes(q)
    );
  }, [projets, recherche]);

  const onSubmit = async (data: FormProjet) => {
    await creerProjet.mutateAsync({
      code: data.code,
      nom: data.nom,
      description: data.description || undefined,
      dateDebut: data.dateDebut,
      dateFin: data.dateFin || undefined,
      idOrganisation: organisation?.idOrganisation ?? '',
    });
    reset();
    setPanelOuvert(false);
  };

  const handleDesactiver = async (projet: Projet) => {
    const ok = await confirm({
      title: `Désactiver le projet "${projet.nom}" ?`,
      message: `Le projet ${projet.code} sera marqué comme inactif. Vous pourrez le réactiver à tout moment.`,
      variant: 'warning',
      labelConfirm: 'Désactiver',
    });
    if (!ok) return;
    await desactiverProjet.mutateAsync(projet.idProjet);
  };

  const handleReactiver = async (projet: Projet) => {
    const ok = await confirm({
      title: `Réactiver le projet "${projet.nom}" ?`,
      message: `Le projet ${projet.code} sera à nouveau actif et disponible.`,
      variant: 'info',
      labelConfirm: 'Réactiver',
    });
    if (!ok) return;
    await reactiverProjet.mutateAsync(projet.idProjet);
  };

  const formaterDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-content tracking-tight">Projets</h1>
          {organisation?.nom && (
            <div className="flex items-center gap-1.5 mt-1">
              <Building2 className="w-3.5 h-3.5 text-content-subtle" />
              <span className="text-sm text-content-subtle">{organisation.nom}</span>
            </div>
          )}
          {!organisation?.nom && (
            <p className="text-sm text-content-subtle mt-1">
              Gestion des projets et affectations d'employés
            </p>
          )}
        </div>
        <button
          onClick={() => setPanelOuvert(!panelOuvert)}
          className="inline-flex items-center gap-2 bg-primary text-content-inverted px-5 py-2.5 rounded-xl
                     hover:bg-primary-hover active:bg-primary-hover transition-all duration-150
                     shadow-sm hover:shadow-md text-sm font-semibold"
        >
          <Plus className="w-4 h-4" />
          Nouveau projet
        </button>
      </div>

      {/* ── Panel création ─────────────────────────────────────── */}
      {panelOuvert && (
        <div className="mb-8 bg-surface border border-border rounded-2xl shadow-sm overflow-hidden
                        animate-[slideDown_200ms_ease-out]">
          <div className="flex items-center justify-between px-6 py-4 bg-surface-raised border-b border-border">
            <h2 className="text-base font-semibold text-content">Nouveau projet</h2>
            <button
              onClick={() => { setPanelOuvert(false); reset(); }}
              className="text-content-subtle hover:text-content-muted transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Code */}
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1.5">
                <Hash className="w-3.5 h-3.5 inline mr-1 text-content-subtle" />
                Code projet
              </label>
              <input
                {...register('code', { required: 'Le code est requis' })}
                placeholder="EX: PRJ-001"
                className="w-full border border-border rounded-lg px-3.5 py-2.5 text-sm font-mono
                           placeholder:text-content-subtle bg-surface text-content
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                           transition-shadow"
              />
              {errors.code && <p className="text-danger text-xs mt-1">{errors.code.message}</p>}
            </div>

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1.5">Nom du projet</label>
              <input
                {...register('nom', { required: 'Le nom est requis' })}
                placeholder="Projet Caritas Borgou"
                className="w-full border border-border rounded-lg px-3.5 py-2.5 text-sm bg-surface text-content
                           placeholder:text-content-subtle
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                           transition-shadow"
              />
              {errors.nom && <p className="text-danger text-xs mt-1">{errors.nom.message}</p>}
            </div>

            {/* Description (pleine largeur) */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-content-muted mb-1.5">Description</label>
              <textarea
                {...register('description')}
                rows={2}
                placeholder="Description optionnelle du projet..."
                className="w-full border border-border rounded-lg px-3.5 py-2.5 text-sm resize-none bg-surface text-content
                           placeholder:text-content-subtle
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                           transition-shadow"
              />
            </div>

            {/* Date début */}
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1.5">
                <Calendar className="w-3.5 h-3.5 inline mr-1 text-content-subtle" />
                Date de début
              </label>
              <input
                type="date"
                {...register('dateDebut', { required: 'La date de début est requise' })}
                className="w-full border border-border rounded-lg px-3.5 py-2.5 text-sm bg-surface text-content
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                           transition-shadow"
              />
              {errors.dateDebut && <p className="text-danger text-xs mt-1">{errors.dateDebut.message}</p>}
            </div>

            {/* Date fin */}
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1.5">
                <Calendar className="w-3.5 h-3.5 inline mr-1 text-content-subtle" />
                Date de fin (optionnelle)
              </label>
              <input
                type="date"
                {...register('dateFin')}
                className="w-full border border-border rounded-lg px-3.5 py-2.5 text-sm bg-surface text-content
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                           transition-shadow"
              />
            </div>

            {/* Erreur globale */}
            {creerProjet.isError && (
              <div className="md:col-span-2 text-sm text-danger bg-danger-light border border-danger rounded-lg p-3">
                Erreur lors de la création du projet. Veuillez réessayer.
              </div>
            )}

            {/* Actions */}
            <div className="md:col-span-2 flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => { setPanelOuvert(false); reset(); }}
                className="px-4 py-2.5 text-sm font-medium text-content-muted bg-surface-overlay
                           hover:bg-surface-raised rounded-lg transition-colors border border-border"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2.5 text-sm font-semibold text-content-inverted bg-primary
                           hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed
                           rounded-lg transition-all shadow-sm"
              >
                {isSubmitting ? 'Création...' : 'Créer le projet'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Filtres ────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle" />
          <input
            type="text"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher par nom ou code..."
            className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm bg-surface text-content
                       placeholder:text-content-subtle
                       focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                       transition-shadow"
          />
        </div>
        <select
          value={filtreStatut}
          onChange={(e) => setFiltreStatut(e.target.value as FiltreStatut)}
          className="border border-border rounded-lg px-3.5 py-2.5 text-sm text-content-muted bg-surface
                     focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                     transition-shadow min-w-[160px]"
        >
          <option value="tous">Tous les statuts</option>
          <option value="actif">Actifs uniquement</option>
          <option value="inactif">Inactifs uniquement</option>
        </select>
      </div>

      {/* ── État : chargement ──────────────────────────────────── */}
      {isLoading && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl p-5 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-surface-overlay rounded-lg" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-surface-overlay rounded w-1/3" />
                  <div className="h-3 bg-surface-raised rounded w-1/2" />
                </div>
                <div className="h-6 w-16 bg-surface-overlay rounded-full" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── État : erreur ──────────────────────────────────────── */}
      {isError && (
        <div className="bg-danger-light border border-danger rounded-xl p-6 text-center">
          <p className="text-danger font-medium">Erreur lors du chargement des projets</p>
          <p className="text-danger text-sm mt-1 opacity-80">
            {error instanceof Error ? error.message : 'Veuillez réessayer ultérieurement.'}
          </p>
        </div>
      )}

      {/* ── État : liste vide ──────────────────────────────────── */}
      {!isLoading && !isError && projetsFiltres.length === 0 && (
        <div className="bg-surface border border-border rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-surface-raised rounded-2xl flex items-center justify-center">
            <FolderOpen className="w-8 h-8 text-content-subtle" />
          </div>
          <h3 className="text-lg font-semibold text-content-muted mb-1">Aucun projet trouvé</h3>
          <p className="text-sm text-content-subtle mb-6">
            {recherche
              ? `Aucun projet ne correspond à "${recherche}".`
              : 'Commencez par créer votre premier projet.'}
          </p>
          {!recherche && (
            <button
              onClick={() => setPanelOuvert(true)}
              className="inline-flex items-center gap-2 bg-primary text-content-inverted px-5 py-2.5
                         rounded-xl hover:bg-primary-hover transition-colors text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />
              Créer un projet
            </button>
          )}
        </div>
      )}

      {/* ── Tableau des projets ────────────────────────────────── */}
      {!isLoading && !isError && projetsFiltres.length > 0 && (
        <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr className="bg-surface-raised">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">
                    Date début
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">
                    Date fin
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold text-content-subtle uppercase tracking-wider">
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {projetsFiltres.map((projet: Projet) => (
                  <tr
                    key={projet.idProjet}
                    className="hover:bg-surface-raised transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary-light flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-4 h-4 text-primary-text" />
                        </div>
                        <span className="text-sm font-mono font-semibold text-content">
                          {projet.code}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-content">{projet.nom}</p>
                        {projet.description && (
                          <p className="text-xs text-content-subtle mt-0.5 line-clamp-1">
                            {projet.description}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-content-muted">
                      {formaterDate(projet.dateDebut)}
                    </td>
                    <td className="px-6 py-4 text-sm text-content-muted">
                      {projet.dateFin ? formaterDate(projet.dateFin) : (
                        <span className="text-content-subtle italic">En cours</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {projet.actif ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                                       bg-success-light text-success border border-success">
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                                       bg-surface-overlay text-content-subtle border border-border">
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-1 items-center justify-end">
                        <IconButton
                          icon={Eye}
                          label="Voir"
                          href={`/paie/administration/projets/${projet.idProjet}`}
                        />
                        {projet.actif ? (
                          <IconButton
                            icon={Archive}
                            label="Désactiver"
                            variant="warning"
                            onClick={() => handleDesactiver(projet)}
                          />
                        ) : (
                          <IconButton
                            icon={RefreshCw}
                            label="Réactiver"
                            variant="success"
                            onClick={() => handleReactiver(projet)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pied de tableau */}
          <div className="px-6 py-3 bg-surface-raised border-t border-border">
            <p className="text-xs text-content-subtle">
              {projetsFiltres.length} projet{projetsFiltres.length > 1 ? 's' : ''}
              {recherche && ` pour "${recherche}"`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
